import React, { useEffect, useState, useMemo } from 'react';
import { ProSidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import clientsIcon from '../icons/users.svg';
import userIcon from '../icons/user.svg';
import purchasesIcon from '../icons/corner-down-left.svg';
import catalogIcon from '../icons/book-open.svg';
import dashboardIcon from '../icons/grid.svg';
import ordersIcon from '../icons/package.svg';
import repoIcon from '../icons/layers.svg';
import chevronUp from '../icons/chevron-upG.svg';
import chevronDown from '../icons/chevron-downG.svg';
import logoGF from '../images/logo.png';
import paymentIcon from '../icons/payment.svg';
import performanceIcon from '../icons/performance.svg';

const Sidebar = () => {
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const { t } = useTranslation();

  const handleImageError = (e) => {
    e.target.src = logoGF;
    e.target.style.width = "150px";
    e.target.style.height = "50px";
  };

  const roleBasedModules = {
    ROLE_ADMIN: {
      catalog: ['brands', 'families', 'products'],
      clients: ['list'],
      purchases: ['history'],
      sales: ['quoteList', 'orderList', 'deliveryNotesList', 'creditNotesList', 'afournisseurList', 'afinancierList'],
      recouvrement: ['payments', 'infosFinancieres'],
      performance: ['expenses'],
      repo: ['stock', 'repoAdjustments', 'transfers', 'transferRequests', 'providers', 'repos'],
      administration: ['users', 'extraFields', 'banks', 'paymentTypes'],
    },
    ROLE_AGENT: {
      catalog: ['brands', 'families', 'products'],
      clients: ['list'],
      purchases: ['history'],
      sales: ['quoteList', 'orderList', 'deliveryNotesList', 'creditNotesList', 'afournisseurList', 'afinancierList'],
      recouvrement: ['payments', 'infosFinancieres'],
      performance: ['expenses'],
      repo: ['stock', 'repoAdjustments'],
    },
    ROLE_COMMERCIAL: {
      catalog: ['brands', 'families', 'products'],
      clients: ['list'],
      sales: ['quoteList', 'orderList', 'deliveryNotesList', 'creditNotesList', 'afinancierList'],
      recouvrement: ['payments', 'infosFinancieres'],
      performance: ['expenses'],
      repo: ['stock', 'transferRequests'],
    },
    ROLE_LOGISTIQUE: {
      catalog: ['brands', 'families', 'products'],
      clients: ['list'],
      sales: ['orderList', 'deliveryNotesList'],
      recouvrement: ['payments', 'infosFinancieres'],
      performance: ['expenses'],
      repo: ['stock'],
    },
  };

  const submenus = useMemo(() => [
    {
      title: t(`titles.catalog`),
      key: 'catalog',
      icon: catalogIcon,
      items: [
        { title: t(`titles.brands`), path: '/catalog/brands', key: 'brands' },
        { title: t(`titles.families`), path: '/catalog/families', key: 'families' },
        { title: t(`titles.products`), path: '/catalog/products', key: 'products' },
      ],
    },
    {
      title: t(`titles.clients`),
      key: 'clients',
      icon: clientsIcon,
      items: [{ title: t(`titles.sb_clients`), path: '/clients', key: 'list' }],
    },
    {
      title: t(`titles.sb_purchases`),
      key: 'purchases',
      icon: purchasesIcon,
      items: [{ title: t(`titles.purchases`), path: '/purchases', key: 'history' }],
    },
    {
      title: t(`titles.sales`),
      key: 'sales',
      icon: ordersIcon,
      items: [
        { title: t(`titles.quotes`), path: '/quotes', key: 'quoteList' },
        { title: t(`titles.orders`), path: '/orders', key: 'orderList' },
        { title: t(`titles.bl`), path: '/delivery-notes', key: 'deliveryNotesList' },
        { title: t(`titles.br`), path: '/credit-notes', key: 'creditNotesList' },
        { title: t(`titles.afinancier`), path: '/avoirs-financiers', key: 'afinancierList' },
        { title: t(`titles.afournisseur`), path: '/avoirs-fournisseurs', key: 'afournisseurList' },
      ],
    },
    {
      title: t(`titles.recouvrement`),
      key: 'recouvrement',
      icon: paymentIcon,
      items: [
        { title: t(`titles.payments`), path: '/payments', key: 'payments' },
        { title: t(`titles.infosFinancieres`), path: '/infos-financiers', key: 'infosFinancieres' },
      ],
    },
    {
      title: t(`titles.performance`),
      key: 'performance',
      icon: performanceIcon,
      items: [{ title: t(`titles.expenses`), path: '/expenses', key: 'expenses' }],
    },
    {
      title: t(`titles.repos`),
      key: 'repo',
      icon: repoIcon,
      items: [
        { title: t(`titles.repo`), path: '/products-by-repo', key: 'stock' },
        { title: t(`titles.stock_adjustments`), path: '/stock-adjustments', key: 'repoAdjustments' },
        { title: t(`titles.transfers`), path: '/transfers/history', key: 'transfers' },
        { title: t(`titles.requests`), path: '/transfer-requests', key: 'transferRequests' },
        { title: t(`titles.sb_providers`), path: '/providers', key: 'providers' },
        { title: t(`titles.sb_repositories`), path: '/repos', key: 'repos' },
      ],
    },
    {
      title: t(`titles.admin`),
      key: 'administration',
      icon: userIcon,
      items: [
        { title: t(`titles.sb_users`), path: '/users', key: 'users' },
        { title: t(`titles.sb_config`), path: '/fields', key: 'extraFields' },
        { title: t(`titles.banks`), path: '/banks', key: 'banks' },
        { title: t(`titles.paymentTypes`), path: '/payment-types', key: 'paymentTypes' },
      ],
    },
  ], [t]);

  useEffect(() => {
    const roleStore = localStorage.getItem('user');
    const role = roleStore ? JSON.parse(roleStore).role : null;
    setUserRole(role);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    const matchingMenu = submenus.find((submenu) =>
      submenu.items.some((item) => currentPath.startsWith(item.path))
    );

    if (matchingMenu) {
      setExpandedMenu(matchingMenu.key);
    }
  }, [location.pathname, submenus]);

  const handleToggle = (menuName) => {
    setExpandedMenu(expandedMenu === menuName ? null : menuName);
  };

  const isActiveSubMenu = (path) => location.pathname.startsWith(path);

  const purpleFilter =
    'brightness(0) saturate(100%) invert(36%) sepia(81%) saturate(4000%) hue-rotate(225deg) brightness(95%) contrast(90%)';
  const defaultFilter = 'none';

  const filteredSubmenus = submenus
    .map((submenu) => ({
      ...submenu,
      items: submenu.items.filter(
        (item) => roleBasedModules[userRole]?.[submenu.key]?.includes(item.key)
      ),
    }))
    .filter((submenu) => submenu.items.length > 0);

  const logo = localStorage.getItem('logo') ? localStorage.getItem('logo') : logoGF;

  return (
    <ProSidebar
      className="h-screen overflow-y-auto bg-[#F9FAFC] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo Section */}
      <div className="flex items-center justify-center h-[140px]">
        <img src={logo} alt="Company Logo" className="h-10" onError={handleImageError} />
      </div>

      <Menu iconShape="square">
        {/* Dashboard */}
        {userRole !== 'ROLE_LOGISTIQUE' && (
          <MenuItem
            active={location.pathname === '/'}
            onClick={() => setExpandedMenu(null)}
            className={`flex items-center px-4 py-3 ${location.pathname === '/'
              ? 'bg-[rgba(107,120,248,0.15)] text-[#6A78F8]'
              : 'text-[#231E29]'
              }`}
            style={{
              borderRadius: '8px',
              margin: '0 10px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Link to="/" className="w-full h-full flex items-center">
              <span className="flex items-center gap-3">
                <img
                  src={dashboardIcon}
                  alt="Dashboard"
                  style={{
                    filter: location.pathname === '/' ? purpleFilter : defaultFilter,
                  }}
                />
                <span className={`${location.pathname === '/' ? 'font-semibold' : 'font-medium'}`}>{t(`titles.dashboard`)}</span>
              </span>
            </Link>
          </MenuItem>
        )}

        {/* Dynamic Submenus */}
        {filteredSubmenus.map((submenu) => (
          <div key={submenu.key} className="flex flex-col w-full">
            {/* Parent Menu */}
            <MenuItem
              onClick={() => handleToggle(submenu.key)}
              className={`px-4 py-3 ${expandedMenu === submenu.key
                ? 'bg-[rgba(107,120,248,0.15)] text-[#6A78F8]'
                : 'text-[#231E29]'
                }`}
              style={{
                borderRadius: '8px',
                margin: '0 10px',
              }}
            >
              <div className="flex items-center justify-between w-full">
                {/* Title and Icon */}
                <div className="flex items-center gap-3">
                  <img
                    src={submenu.icon}
                    alt="Icon"
                    style={{
                      filter: expandedMenu === submenu.key ? purpleFilter : defaultFilter,
                    }}
                  />
                  <span className={`${expandedMenu === submenu.key ? 'font-semibold' : 'font-medium'}`}>{submenu.title}</span>
                </div>
                {/* Chevron */}
                <div className="ml-auto">
                  <img
                    src={expandedMenu === submenu.key ? chevronUp : chevronDown}
                    alt="Chevron"
                    className="w-5 h-5"
                  />
                </div>
              </div>
            </MenuItem>

            {/* Submenu Items */}
            {expandedMenu === submenu.key && (
              <div className="pl-8 pt-2">
                {submenu.items.map((item) => (
                  <MenuItem
                    key={item.path}
                    active={isActiveSubMenu(item.path)}
                    className={`relative px-4 py-2 ${isActiveSubMenu(item.path)
                      ? 'text-[#6A78F8] bg-transparent before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[4px] before:bg-[#6A78F8]'
                      : 'text-[#231E29] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[1px] before:bg-gray-300'
                      }`}
                  >
                    <Link to={item.path} className={`${isActiveSubMenu(item.path) ? 'font-semibold' : 'font-medium'} w-full h-full flex items-center hover:text-[#6A78F8] transition-colors`}>
                    {item.title}</Link>
                  </MenuItem>
                ))}
              </div>
            )}
          </div>
        ))}
      </Menu>

    </ProSidebar>
  );
};

export default Sidebar;
